// Invite-a-friend helpers.
//
// On mobile (iOS Safari, Android Chrome) the Web Share API opens the
// native share sheet — WhatsApp, SMS, email, AirDrop, whatever the user
// has set up — pre-filled with our message. On desktop browsers without
// a share sheet, we fall back to copying the message to the clipboard so
// the user can paste it anywhere.
//
// The message includes the inviter's @username so the recipient knows
// who's writing and who to add back once they're in.

export const NO_RUSH_APP_URL = 'https://app.norush.chat';

/**
 * The pre-filled invite text. Kept short on purpose — long messages get
 * truncated in SMS previews and feel like marketing in iMessage. The
 * tone is plain, in Ermis voice, no buzzwords.
 */
export function buildInviteMessage(username: string): string {
  return `I'm on No Rush. Calm chat, no notifications, no read receipts. Open ${NO_RUSH_APP_URL} and add me as @${username}.`;
}

export type InviteResult = 'shared' | 'copied' | 'cancelled';

/**
 * Tries navigator.share first (native share sheet on mobile + Safari);
 * falls back to clipboard.
 *
 * Returns:
 *   - 'shared'    — user picked a share target and it dispatched
 *   - 'cancelled' — user dismissed the native sheet (treat as no-op)
 *   - 'copied'    — share API unavailable, we wrote to clipboard instead
 *
 * Throws only on the rare case where clipboard.writeText is unavailable
 * (e.g. an HTTP context — but No Rush is always HTTPS so this is
 * defensive, not expected).
 */
export async function shareInvite(username: string): Promise<InviteResult> {
  const text = buildInviteMessage(username);

  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: 'No Rush', text });
      return 'shared';
    } catch (err) {
      const name = (err as { name?: string }).name;
      // AbortError = user dismissed the sheet. Don't fall through to
      // clipboard in that case — they explicitly chose not to share.
      if (name === 'AbortError') return 'cancelled';
      // Otherwise (NotAllowedError, etc.) fall through to clipboard.
    }
  }

  await navigator.clipboard.writeText(text);
  return 'copied';
}
